/* Brellova product hero
   <rn-buy-box>     variant + quantity-tier selection, price display, add to cart (incl. mixed colours)
   <rn-gallery>     mobile carousel progress, variant image to front, lightbox
   <rn-sticky-atc>  bottom bar while the main button is off screen
*/
(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* Translated strings come from window.rnStrings (layout/theme.liquid, locales → brellova.*);
     the English fallback keeps the page working if the object is missing. */
  function str(key, fallback) {
    return (window.rnStrings && window.rnStrings[key]) || fallback;
  }

  // "2 umbrellas" / "2 parasole" / "5 parasoli": pick the plural form for the page language
  function umbrellas(n) {
    const forms = (window.rnStrings && window.rnStrings.umbrellas) || {};
    let cat = 'other';
    try {
      cat = new Intl.PluralRules(document.documentElement.lang || 'en').select(n);
    } catch (e) {}
    const form = forms[cat] || forms.other || (n === 1 ? '[count] umbrella' : '[count] umbrellas');
    return form.replace('[count]', n);
  }

  function formatMoney(cents, format) {
    const value = typeof cents === 'string' ? parseInt(cents.replace('.', ''), 10) : cents;
    const placeholder = /\{\{\s*(\w+)\s*\}\}/;
    const fmt = format || '€{{amount_with_comma_separator}}';
    const withDelimiters = (number, precision, thousands = ',', decimal = '.') => {
      if (isNaN(number) || number == null) return '0';
      const fixed = (number / 100).toFixed(precision);
      const [whole, frac] = fixed.split('.');
      const w = whole.replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
      return frac ? `${w}${decimal}${frac}` : w;
    };
    const match = fmt.match(placeholder);
    if (!match) return fmt;
    let out;
    switch (match[1]) {
      case 'amount':
        out = withDelimiters(value, 2);
        break;
      case 'amount_no_decimals':
        out = withDelimiters(value, 0);
        break;
      case 'amount_with_comma_separator':
        out = withDelimiters(value, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        out = withDelimiters(value, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        out = withDelimiters(value, 2, "'", '.');
        break;
      case 'amount_with_space_separator':
        out = withDelimiters(value, 2, ' ', ',');
        break;
      default:
        out = withDelimiters(value, 2);
    }
    return fmt.replace(placeholder, out);
  }

  /* Mirrors Shopify's percentage discount: each unit's discount is rounded down to the cent
     (verified against the cart: 3 × 29,99 at 25% → 67,50, not 67,48) */
  function discountedTotal(units, keep) {
    return units.reduce((sum, v) => sum + v.price - Math.floor((v.price * (100 - keep)) / 100), 0);
  }

  /* ---------------- Buy box ---------------- */

  class RnBuyBox extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;

      this.data = JSON.parse(this.querySelector('[data-product-json]').textContent);
      this.form = this.querySelector('form');
      this.variantInput = this.querySelector('[data-variant-input]');
      this.quantityInput = this.querySelector('[data-quantity-input]');
      this.atc = this.querySelector('[data-atc]');
      this.errorEl = this.querySelector('[data-form-error]');
      this.express = this.querySelector('[data-express]');
      this.mixEl = this.querySelector('[data-mix]');
      this.mixRows = this.querySelector('[data-mix-rows]');

      this.variantInput.disabled = false;
      const initialId = Number(this.variantInput.value);
      this.variant = this.data.variants.find((v) => v.id === initialId) || this.data.variants[0];
      this.qty = 1;
      this.keep = 100;
      this.mix = null; // array of variant ids, one per unit, when colours are mixed

      this.addEventListener('change', (e) => this.onChange(e));
      this.form.addEventListener('submit', (e) => {
        e.preventDefault();
        this.addToCart();
      });

      this.setupStickyTop();
      this.setupExpress();
      this.render();
    }

    /* Express checkout (Shop Pay, PayPal, …) arrives as a <template> (see rn-product-hero.liquid). Add it once the page
       has loaded and the buttons are near the screen, or as soon as the visitor touches the buy box. */
    setupExpress() {
      const later = this.express?.querySelector('template[data-express-later]');
      if (!later) return;
      let io = null;
      const show = () => {
        io?.disconnect();
        this.removeEventListener('pointerdown', show);
        this.removeEventListener('focusin', show);
        if (later.isConnected) later.replaceWith(later.content.cloneNode(true));
      };
      this.addEventListener('pointerdown', show);
      this.addEventListener('focusin', show);
      const watch = () => {
        if (!('IntersectionObserver' in window)) return show();
        io = new IntersectionObserver((entries) => entries.some((e) => e.isIntersecting) && show(), {
          rootMargin: '300px 0px',
        });
        io.observe(this.express);
      };
      if (document.readyState === 'complete') watch();
      else window.addEventListener('load', watch, { once: true });
    }

    /* Colour hex for a variant, read from its swatch label so the theme editor stays the source of truth */
    swatchFor(variant) {
      const value = variant.options[0];
      const input = [...this.querySelectorAll('[data-option-input="1"]')].find((i) => i.value === value);
      const label = input && this.querySelector(`label[for="${input.id}"]`);
      return label ? label.style.getPropertyValue('--swatch') : '';
    }

    onChange(e) {
      const t = e.target;
      if (t.matches('[data-option-input]')) {
        const selected = [...this.querySelectorAll('[data-option-index]')].map(
          (fs) => fs.querySelector('[data-option-input]:checked')?.value
        );
        const match = this.data.variants.find((v) => v.options.every((o, i) => o === selected[i]));
        if (match) {
          this.variant = match;
          // A new main colour sets every umbrella to that colour
          if (this.mix) this.mix = this.mix.map(() => match.id);
          this.syncMix();
          this.moveMediaToFront(match.mediaId);
          this.updateUrl();
        }
      } else if (t.name === 'rn-tier') {
        this.qty = Number(t.value);
        this.keep = Number(t.dataset.keep || 100);
        this.syncMix();
      } else if (t.matches('[data-mix-input]')) {
        const unit = Number(t.dataset.mixInput);
        if (!this.mix) this.mix = Array.from({ length: this.qty }, () => this.variant.id);
        this.mix[unit] = Number(t.value);
      }
      this.render();
    }

    get isMixed() {
      return !!this.mix && this.qty > 1 && new Set(this.mix).size > 1;
    }

    unitsVariants() {
      if (this.mix && this.qty > 1) return this.mix.map((id) => this.data.variants.find((v) => v.id === id));
      return Array.from({ length: this.qty }, () => this.variant);
    }

    totals() {
      const units = this.unitsVariants();
      // Reference for the struck-through tier price: the same umbrellas at their current single price
      const ref = units.reduce((sum, v) => sum + v.price, 0);
      return { total: discountedTotal(units, this.keep), ref, available: units.every((v) => v && v.available) };
    }

    money(cents) {
      return formatMoney(cents, this.data.moneyFormat);
    }

    render() {
      const v = this.variant;
      const fmt = (c) => this.money(c);

      // Option value labels + hidden inputs
      this.variantInput.value = v.id;
      this.quantityInput.value = this.qty;
      v.options.forEach((val, i) => {
        const el = this.querySelector(`[data-option-value="${i + 1}"]`);
        if (el) el.textContent = val;
      });

      // Unit price line
      const now = this.querySelector('[data-price-now]');
      const was = this.querySelector('[data-price-was]');
      const save = this.querySelector('[data-price-save]');
      now.textContent = fmt(v.price);
      const onSale = v.compare > v.price;
      was.hidden = !onSale;
      save.hidden = !onSale;
      if (onSale) {
        const sr = document.createElement('span');
        sr.className = 'rn-visually-hidden';
        sr.textContent = str('regularPrice', 'Regular price');
        was.replaceChildren(sr, document.createTextNode(fmt(v.compare)));
        save.textContent = str('saveAmount', 'Save [amount]').replace('[amount]', fmt(v.compare - v.price));
      }

      // Tiers — priced for the current colour (or the mix, for the selected tier)
      const swatch = this.swatchFor(v);
      this.style.setProperty('--rn-current-swatch', swatch);
      this.querySelectorAll('input[name="rn-tier"]').forEach((input) => {
        const q = Number(input.value);
        const keep = Number(input.dataset.keep || 100);
        const label = this.querySelector(`label[for="${input.id}"]`);
        const useMix = q === this.qty && this.mix && q > 1;
        const units = useMix ? this.unitsVariants() : Array.from({ length: q }, () => v);
        const ref = units.reduce((s, u) => s + u.price, 0);
        const total = discountedTotal(units, keep);
        label.querySelector('[data-tier-total]').textContent = fmt(total);
        const refEl = label.querySelector('[data-tier-ref]');
        refEl.hidden = !(ref > total);
        if (ref > total) refEl.textContent = fmt(ref);
        const each = label.querySelector('[data-tier-each]');
        if (each) each.textContent = str('each', '[price] each').replace('[price]', fmt(Math.floor(total / q)));
        const caps = label.querySelectorAll('.rn-tier__unit');
        caps.forEach((c, i) => c.style.setProperty('--swatch', useMix ? this.swatchFor(units[i]) : swatch));
      });

      const note = this.querySelector('[data-tiers-note]');
      if (note) note.hidden = !(this.qty > 1 && this.keep < 100);

      // Button
      const { total, available } = this.totals();
      this.atc.disabled = !available;
      this.querySelector('[data-atc-label]').textContent = available ? str('addToCart', 'Add to cart') : str('soldOut', 'Sold out');
      this.querySelector('[data-atc-price]').textContent = fmt(total);

      // Express checkout buys one variant × quantity, so it can't represent a mixed order
      this.express?.classList.toggle('is-disabled', this.isMixed || !available);

      this.dispatchEvent(
        new CustomEvent('rn:change', {
          bubbles: true,
          detail: {
            available,
            total: fmt(total),
            summary: this.summary(),
            image: v.image,
          },
        })
      );
    }

    summary() {
      const n = this.qty;
      const count = umbrellas(n);
      if (this.isMixed) return str('mixed', '[umbrellas], mixed colours').replace('[umbrellas]', count);
      const name = this.data.variants.length > 1 ? this.variant.title : '';
      return [name, n > 1 ? count : ''].filter(Boolean).join(', ') || this.data.title;
    }

    /* Per-umbrella colours: open for 2+ umbrellas, every umbrella starting in the main colour, and placed
       right under the selected option card. */
    syncMix() {
      if (!this.mixEl) return;
      if (this.qty > 1) {
        this.mix = Array.from({ length: this.qty }, (_, i) => (this.mix && this.mix[i]) || this.variant.id);
        const card = this.querySelector(`label[data-tier="${this.qty}"]`);
        if (card && card.nextElementSibling !== this.mixEl) card.after(this.mixEl);
        this.mixEl.hidden = false;
        this.renderMixRows();
      } else {
        this.mix = null;
        this.mixEl.hidden = true;
      }
    }

    renderMixRows() {
      if (!this.mixRows || !this.mix) return;
      const variants = this.data.variants;
      this.mixRows.innerHTML = this.mix
        .map((selectedId, unit) => {
          const swatches = variants
            .map((v) => {
              const id = `${this.id}-mix-${unit}-${v.id}`;
              return `<input type="radio" class="rn-choice__input" id="${id}" name="rn-mix-${unit}" value="${v.id}" data-mix-input="${unit}" ${
                v.id === selectedId ? 'checked' : ''
              } ${v.available ? '' : 'disabled'}>
              <label for="${id}" class="rn-swatch${v.available ? '' : ' is-unavailable'}" style="--swatch:${this.swatchFor(v)}" title="${v.title}">
                <span class="rn-visually-hidden">${v.title}</span></label>`;
            })
            .join('');
          const label = str('mixUnit', 'Umbrella [number]').replace('[number]', unit + 1);
          return `<fieldset class="rn-mix__row"><legend><span class="rn-mix__num" aria-hidden="true">${unit + 1}</span><span class="rn-visually-hidden">${label}</span></legend><div class="rn-swatches">${swatches}</div></fieldset>`;
        })
        .join('');
    }

    moveMediaToFront(mediaId) {
      if (!mediaId) return;
      const gallery = document.querySelector(`rn-gallery[data-section="${this.dataset.section}"]`);
      gallery?.moveToFront(mediaId);
    }

    updateUrl() {
      if (!window.location.pathname.includes('/products/')) return;
      const url = new URL(window.location.href);
      url.searchParams.set('variant', this.variant.id);
      window.history.replaceState({}, '', url.toString());
    }

    /* Sticky buy box that is taller than the viewport pins by its bottom edge instead */
    setupStickyTop() {
      const wrap = this.closest('.rn-buy-wrap');
      if (!wrap) return;
      let h = 0;
      const update = () => {
        const headerH = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--rn-header-h')) || 64;
        const fitsTop = Math.min(headerH + 24, window.innerHeight - h - 24);
        wrap.style.setProperty('--rn-buy-top', `${fitsTop}px`);
      };
      // The observer reports the height once the browser has laid the page out (first report before the first
      // paint), so nothing here forces an early layout while the page is loading
      new ResizeObserver(([entry]) => {
        h = entry.borderBoxSize?.[0]?.blockSize ?? wrap.offsetHeight;
        update();
      }).observe(wrap);
      window.addEventListener('resize', update, { passive: true });
    }

    setLoading(on) {
      [this.atc, document.querySelector('[data-sticky-atc]')].forEach((b) => {
        if (!b) return;
        b.classList.toggle('is-loading', on);
        b.setAttribute('aria-disabled', on ? 'true' : 'false');
      });
    }

    showError(message) {
      if (!this.errorEl) return;
      this.errorEl.hidden = !message;
      this.errorEl.textContent = message || '';
    }

    async addToCart() {
      if (this.atc.disabled || this.atc.getAttribute('aria-disabled') === 'true') return;
      this.showError(null);
      this.setLoading(true);

      const counts = new Map();
      this.unitsVariants().forEach((v) => counts.set(v.id, (counts.get(v.id) || 0) + 1));
      const items = [...counts].map(([id, quantity]) => ({ id, quantity }));

      const drawer = document.querySelector('cart-drawer');
      const body = { items };
      if (drawer) {
        body.sections = drawer.getSectionsToRender().map((s) => s.id);
        body.sections_url = window.location.pathname;
        drawer.setActiveElement(document.activeElement);
      }

      try {
        const res = await fetch(`${window.routes.cart_add_url}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify(body),
        });
        const json = await res.json();
        if (!res.ok || json.status) {
          this.showError(json.description || json.message || str('addError', 'This item could not be added to the cart.'));
          return;
        }
        if (!drawer) {
          window.location = window.routes.cart_url;
          return;
        }
        if (typeof publish === 'function' && window.PUB_SUB_EVENTS) {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'rn-buy-box', productVariantId: this.variant.id, cartData: json });
        }
        drawer.classList.remove('is-empty');
        drawer.renderContents({ ...json, id: this.variant.id });
      } catch (err) {
        console.error(err);
        this.showError('Connection problem. Check your connection and try again.');
      } finally {
        this.setLoading(false);
      }
    }
  }

  /* ---------------- Gallery ---------------- */

  class RnGallery extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this.list = this.querySelector('[data-gallery-list]');
      this.current = this.querySelector('[data-gallery-current]');
      this.bar = this.querySelector('[data-gallery-bar]');
      this.lightbox = this.querySelector('[data-lightbox]');
      this.colourChip = this.querySelector('.rn-gallery__colour');
      this.hasStage = !!this.querySelector('[data-stage]');

      const count = this.list.children.length;
      if (this.bar && count) this.bar.style.setProperty('--progress-w', `${100 / count}%`);

      this.ticking = false;
      this.list.addEventListener(
        'scroll',
        () => {
          if (this.ticking) return;
          this.ticking = true;
          requestAnimationFrame(() => {
            this.updateProgress();
            this.ticking = false;
          });
        },
        { passive: true }
      );

      this.addEventListener('click', (e) => {
        const open = e.target.closest('[data-lightbox-open]');
        if (open) this.openLightbox(open.dataset.lightboxOpen, open);
        if (e.target.closest('[data-lightbox-close]') || e.target === this.lightbox) this.lightbox?.close();
      });
      this.lightbox?.addEventListener('close', () => this.lastTrigger?.focus({ preventScroll: true }));

      // Colour stage: let the other colours' photos download once the page is idle,
      // or sooner if the visitor heads for the colour swatches.
      this.stage = this.querySelector('[data-stage]');
      if (this.stage) {
        const ready = () => this.readyStage();
        window.addEventListener('load', () => ('requestIdleCallback' in window ? requestIdleCallback(ready, { timeout: 2500 }) : setTimeout(ready, 1200)), { once: true });
        const swatches = document.querySelector(`rn-buy-box[data-section="${this.dataset.section}"] .rn-swatches`);
        ['pointerenter', 'focusin', 'touchstart'].forEach((ev) => swatches?.addEventListener(ev, ready, { once: true, passive: true }));
      }

      // Photos from the 4th tile on arrive as data-src (rn-media-tile.liquid). Once the page has loaded, or as soon as
      // the visitor touches the carousel, keep about three photos ahead of the visible one downloaded.
      const later = [...this.list.querySelectorAll('img[data-later]')];
      if (later.length) {
        let armed = false;
        const arm = () => {
          if (armed) return;
          armed = true;
          if (!('IntersectionObserver' in window)) return later.forEach((img) => this.loadLater(img));
          const io = new IntersectionObserver(
            (entries) =>
              entries.forEach((entry) => {
                if (!entry.isIntersecting) return;
                io.unobserve(entry.target);
                this.loadLater(entry.target);
              }),
            { root: this.list, rootMargin: '0px 250% 0px 250%' }
          );
          later.forEach((img) => io.observe(img));
        };
        if (document.readyState === 'complete') arm();
        else window.addEventListener('load', arm, { once: true });
        ['pointerdown', 'scroll', 'focusin'].forEach((ev) => this.list.addEventListener(ev, arm, { once: true, passive: true }));
      }
    }

    loadLater(img) {
      img.loading = 'eager';
      if (img.dataset.srcset) img.srcset = img.dataset.srcset;
      if (img.dataset.src) img.src = img.dataset.src;
      img.removeAttribute('data-later');
    }

    updateProgress() {
      const items = [...this.list.children];
      if (!items.length) return;
      // Tiles can differ in width (a portrait video is narrower), so find the tile nearest the scroll position
      const left = this.list.scrollLeft;
      let index = 0;
      const maxLeft = this.list.scrollWidth - this.list.clientWidth;
      if (maxLeft > 2 && left >= maxLeft - 2) {
        index = items.length - 1;
      } else {
        let best = Infinity;
        items.forEach((item, i) => {
          const d = Math.abs(item.offsetLeft - items[0].offsetLeft - left);
          if (d < best) [best, index] = [d, i];
        });
      }
      if (this.current) this.current.textContent = index + 1;
      // "Shown in Dark grey" describes the photos after the colour viewer, so hide it while that is in view
      this.colourChip?.classList.toggle('is-hidden', this.hasStage && index === 0);
      this.bar?.style.setProperty('--progress-x', `${index * 100}%`);
    }

    /* Move the other colours' photos out of their <template> into the stage (starts their download) */
    readyStage() {
      if (!this.stage || this.stage.classList.contains('is-ready')) return;
      const tpl = this.stage.querySelector('template[data-stage-deferred]');
      if (tpl) tpl.replaceWith(tpl.content);
      this.stage.classList.add('is-ready');
    }

    moveToFront(mediaId) {
      const behavior = reduceMotion.matches ? 'auto' : 'smooth';
      // Colour stage: crossfade to that colour's photo in place
      this.readyStage();
      const stageImg = this.stage?.querySelector(`.rn-stage__img[data-media-id="${mediaId}"]`);
      if (stageImg) {
        const prev = this.stage.querySelector('.rn-stage__img.is-active');
        if (prev && prev !== stageImg) {
          // Old photo stays fully visible underneath while the new one fades in on top
          this.stage.querySelectorAll('.rn-stage__img.is-prev').forEach((img) => img.classList.remove('is-prev'));
          prev.classList.remove('is-active');
          prev.classList.add('is-prev');
          const clear = () => prev.classList.remove('is-prev');
          stageImg.addEventListener('transitionend', clear, { once: true });
          setTimeout(clear, 700);
        }
        stageImg.loading = 'eager';
        stageImg.classList.add('is-active');
        this.stage.querySelector('[data-lightbox-open]')?.setAttribute('data-lightbox-open', mediaId);
        if (this.list.scrollWidth > this.list.clientWidth) this.list.scrollTo({ left: 0, behavior });
        return;
      }
      const item = this.list.querySelector(`[data-media-id="${mediaId}"]`);
      if (!item) return;
      // Keep any 3D stage first; the variant image goes right after it
      const anchor = [...this.list.children].find((li) => !li.classList.contains('rn-gallery__item--model'));
      if (anchor && anchor !== item) this.list.insertBefore(item, anchor);
      if (this.list.scrollWidth > this.list.clientWidth) {
        this.list.scrollTo({ left: 0, behavior });
      }
    }

    openLightbox(mediaId, trigger) {
      if (!this.lightbox) return;
      this.lastTrigger = trigger;
      this.lightbox.showModal();
      const target = this.lightbox.querySelector(`#RnLightbox-${mediaId}`);
      target?.querySelectorAll('img').forEach((img) => (img.loading = 'eager'));
      requestAnimationFrame(() => target?.scrollIntoView({ block: 'start' }));
    }
  }

  /* ---------------- Sticky add-to-cart ---------------- */

  class RnStickyAtc extends HTMLElement {
    connectedCallback() {
      this.buyBox = document.getElementById(this.dataset.for);
      if (!this.buyBox) return;
      this.hidden = false;
      this.btn = this.querySelector('[data-sticky-atc]');
      this.atcVisible = true;
      this.footerVisible = false;

      this.btn.addEventListener('click', () => this.buyBox.addToCart());
      this.buyBox.addEventListener('rn:change', (e) => this.sync(e.detail));

      const io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.target === this.buyBox.atc) this.atcVisible = entry.isIntersecting;
          else this.footerVisible = entry.isIntersecting;
        });
        this.update();
      });
      io.observe(this.buyBox.atc);
      const footer = document.querySelector('.shopify-section-group-footer-group');
      if (footer) io.observe(footer);
    }

    sync({ available, total, summary, image }) {
      this.btn.disabled = !available;
      this.querySelector('[data-sticky-label]').textContent = available ? str('addToCart', 'Add to cart') : str('soldOut', 'Sold out');
      this.querySelector('[data-sticky-price]').textContent = total;
      this.querySelector('[data-sticky-meta]').textContent = summary;
      const img = this.querySelector('[data-sticky-img]');
      if (img && image) {
        img.src = image;
        img.removeAttribute('srcset');
      }
    }

    update() {
      const show = !this.atcVisible && !this.footerVisible;
      this.classList.toggle('is-visible', show);
      this.setAttribute('aria-hidden', String(!show));
      this.btn.tabIndex = show ? 0 : -1;
    }
  }

  /* "Shown in Dark grey" labels (snippets/rn-colour-chip.liquid): pick that colour in the buy box,
     as if its swatch was clicked, and bring the colour row into view */
  document.addEventListener('click', (e) => {
    const chip = e.target.closest('[data-select-colour]');
    if (!chip) return;
    const box = document.querySelector('rn-buy-box');
    const input = box && [...box.querySelectorAll('[data-option-input="1"]')].find((i) => i.value === chip.dataset.selectColour);
    if (!input) return;
    if (!input.checked) {
      input.checked = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
    const field = input.closest('[data-option-index]');
    const r = field.getBoundingClientRect();
    if (r.top < 0 || r.bottom > window.innerHeight) {
      field.scrollIntoView({ behavior: reduceMotion.matches ? 'auto' : 'smooth', block: 'center' });
    }
    input.focus({ preventScroll: true });
    field.classList.remove('is-flash');
    void field.offsetWidth; // restart the ring if the label is clicked twice
    field.classList.add('is-flash');
    field.addEventListener('animationend', () => field.classList.remove('is-flash'), { once: true });
  });

  customElements.get('rn-buy-box') || customElements.define('rn-buy-box', RnBuyBox);
  customElements.get('rn-gallery') || customElements.define('rn-gallery', RnGallery);
  customElements.get('rn-sticky-atc') || customElements.define('rn-sticky-atc', RnStickyAtc);
})();
